import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { BarChart2, FolderOpen, FolderArchive } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useAuth } from '../hooks/useAuth';
import { useProjectList, useCreateProject, useUpdateProject, useProjectThroughput, useDeleteProject } from '../api/projects';
import { applyServerErrors } from '../lib/formErrors';
import PageHeader from '../components/layout/PageHeader';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import { CardListSkeleton, Skeleton } from '@/components/ui/skeleton';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import ErrorState from '../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { formatDate } from '../lib/dates';
import type { ProjectDto } from '../types/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false }, title: { display: false } },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true, ticks: { precision: 0 } },
  },
};

interface CreateForm { name: string; description: string; }

function ThroughputChart({ projectId }: { projectId: string }) {
  const { data, isLoading } = useProjectThroughput(projectId);
  if (isLoading) return <Skeleton className="h-32 w-full" />;
  if (!data || data.length === 0) return <p className="py-3 text-center text-xs text-muted-foreground">No throughput data yet.</p>;

  const labels = data.map((w) => {
    const d = new Date(w.weekOf);
    return `${d.toLocaleString('default', { month: 'short' })} ${d.getDate()}`;
  });
  const chartData = {
    labels,
    datasets: [{ data: data.map((w) => w.pointsDelivered), backgroundColor: 'rgb(99, 102, 241)', borderRadius: 4 }],
  };
  return <div className="h-32"><Bar data={chartData} options={CHART_OPTIONS} /></div>;
}

function ProjectCard({ project, canEdit, canDelete, onDelete }: {
  project: ProjectDto; canEdit: boolean; canDelete: boolean;
  onDelete: (id: string, name: string) => void;
}) {
  const [showChart, setShowChart] = useState(false);
  const updateProject = useUpdateProject(project.id);
  const { confirm, dialog } = useConfirm();

  const handleArchive = async () => {
    if (!await confirm({ title: `Archive "${project.name}"?`, description: 'This project will be hidden from active views.', confirmLabel: 'Archive' })) return;
    updateProject.mutate({ archive: true }, { onSuccess: () => toast.success('Project archived.') });
  };

  return (
    <Card className="flex flex-col overflow-hidden p-0 transition-shadow hover:shadow-md">
      {dialog}
      <div className="flex flex-1 flex-col p-5">
        <div className="mb-4 flex items-start justify-between">
          <div className={cn(
            'flex h-10 w-10 items-center justify-center rounded-xl',
            project.isActive ? 'bg-primary/10' : 'bg-muted',
          )}>
            {project.isActive
              ? <FolderOpen className="h-5 w-5 text-primary" />
              : <FolderArchive className="h-5 w-5 text-muted-foreground" />
            }
          </div>
          {!project.isActive && <Badge label="archived" variant="gray" />}
        </div>

        <Link
          to={`/projects/${project.id}`}
          className="font-semibold text-foreground hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          {project.name}
        </Link>
        {project.description && (
          <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{project.description}</p>
        )}

        <p className="mt-auto pt-4 text-xs text-muted-foreground">Created {formatDate(project.createdAt)}</p>

        {showChart && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Delivered points — last 6 weeks</p>
            <ThroughputChart projectId={project.id} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 border-t border-border px-4 py-2.5">
        <button
          type="button"
          className="flex items-center gap-1.5 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
          onClick={() => setShowChart((v) => !v)}
        >
          <BarChart2 className="h-3.5 w-3.5" />
          {showChart ? 'Hide' : 'Throughput'}
        </button>

        <div className="ml-auto flex items-center gap-1">
          {canEdit && project.isActive && (
            <Button variant="ghost" size="sm" onClick={handleArchive} loading={updateProject.isPending}>
              Archive
            </Button>
          )}
          {canDelete && (
            <button
              type="button"
              className="rounded px-2 py-1 text-xs text-destructive hover:bg-destructive/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
              onClick={() => onDelete(project.id, project.name)}
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function ProjectsPage() {
  const { can } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; name: string } | null>(null);

  const { data: projects, isLoading, error, refetch } = useProjectList();
  const createProject = useCreateProject();
  const deleteProject = useDeleteProject();

  const { register, handleSubmit, setError, reset, formState: { errors } } = useForm<CreateForm>();

  const onSubmit = handleSubmit((values) => {
    createProject.mutate(
      { name: values.name, description: values.description || undefined },
      {
        onSuccess: () => { toast.success('Project created.'); reset(); setShowCreate(false); },
        onError:   (e) => applyServerErrors(e, setError),
      },
    );
  });

  if (isLoading) return <CardListSkeleton count={6} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  const active   = projects?.filter((p) => p.isActive)  ?? [];
  const archived = projects?.filter((p) => !p.isActive) ?? [];

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Projects"
        actions={
          can('project_manager') ? (
            <Button size="sm" onClick={() => setShowCreate((v) => !v)}>New project</Button>
          ) : undefined
        }
      />

      {showCreate && (
        <Card className="mb-6 overflow-hidden">
          <div className="flex items-center gap-2.5 border-b border-border px-5 py-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <FolderOpen className="h-4 w-4 text-primary" />
            </div>
            <p className="text-sm font-semibold text-foreground">New project</p>
          </div>
          <form onSubmit={onSubmit}>
            <div className="space-y-4 px-5 py-5">
              <div className="space-y-1.5">
                <Label>Name <span className="text-destructive">*</span></Label>
                <Input {...register('name', { required: 'Name is required' })} />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea rows={2} {...register('description')} />
              </div>
            </div>
            <div className="flex gap-2 border-t border-border bg-muted/30 px-5 py-3">
              <Button size="sm" type="submit" loading={createProject.isPending}>Create</Button>
              <Button size="sm" variant="ghost" type="button" onClick={() => { setShowCreate(false); reset(); }}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {projects?.length === 0 && (
        <EmptyState icon={FolderOpen} title="No projects yet" description="Create a project to start tracking tasks and throughput." />
      )}

      {active.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {active.map((p) => (
            <ProjectCard
              key={p.id} project={p}
              canEdit={can('project_manager')} canDelete={can('head_of_rd')}
              onDelete={(id, name) => setConfirmDelete({ id, name })}
            />
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2">
            <FolderArchive className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-medium text-muted-foreground">Archived</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {archived.map((p) => (
              <ProjectCard
                key={p.id} project={p}
                canEdit={can('project_manager')} canDelete={can('head_of_rd')}
                onDelete={(id, name) => setConfirmDelete({ id, name })}
              />
            ))}
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 shadow-xl">
            <h3 className="text-base font-semibold text-foreground">Delete project?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong className="text-foreground">{confirmDelete.name}</strong> will be permanently deleted. This is only possible if it has no tasks.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button
                size="sm" variant="danger" loading={deleteProject.isPending}
                onClick={() => {
                  deleteProject.mutate(confirmDelete.id, {
                    onSuccess: () => { toast.success('Project deleted.'); setConfirmDelete(null); },
                    onError: (e: unknown) => {
                      const msg = e instanceof Error ? e.message : 'Cannot delete — project has tasks.';
                      toast.error(msg);
                      setConfirmDelete(null);
                    },
                  });
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
